import { createContext, useContext, type ReactNode } from 'react';
import type { FlightAirportSwitchFn } from '../fares/fares_request_page.js';
import type { FareTextClassifier } from '../fares/fare_source.js';
import type { FlightFilterChipsProps } from '../results/filters/flight_filter_chips.js';
import type { FlightOfferPlaces } from '../results/offer/offer_copy.js';
import type { RenderFlightDetail } from '../results/offer/detail_layer.js';
import type { FlightTariffPort } from '../results/offer/offer_list.js';
import type { FlightExplorerCopy } from './explorer_copy.js';

/*
 * O que o explorer recebe do host além do pedido (#4997): os textos, o cadastro
 * de aeroportos, o classificador do texto do fornecedor e as portas (tarifa,
 * telemetria, detalhe do voo, pedido offline, ditado). Tudo chega pela prop
 * `env` da página; por dentro ele desce por um contexto PRIVADO do explorer, no
 * lugar de atravessar cada peça como prop — o kit não exporta o contexto, então
 * nenhum host o provê nem o lê.
 */

/** O cadastro de aeroportos que o explorer lê: o da oferta mais os nomes que só
 *  a página escreve. */
export type FlightExplorerPlaces = FlightOfferPlaces & {
  /** `GRU · Guarulhos` — o rótulo de um aeroporto nos avisos e atalhos. */
  readonly airportLabel: (iata: string) => string;
  /** O nome que a pessoa reconhece: o popular, a cidade ou o IATA cru. */
  readonly airportName: (iata: string) => string;
  /** A cidade no idioma de quem lê (o atalho da cidade inteira). */
  readonly localizedCity: (iata: string) => string;
  /** Os aeroportos de uma cidade metro (`SAO` ⇒ GRU, CGH, VCP); vazio fora dela. */
  readonly metroAirports: (iata: string) => readonly string[];
};

/** O pedido offline à equipe VOLL (#3836): o perfil da busca e o voo em mãos. */
export type FlightOfflineRequestPort = {
  readonly onRequest: (profile: readonly string[], flight: FlightOfflineContext) => void;
};

/** O que o pedido offline leva do voo: só direto e a ida já escolhida. */
export type FlightOfflineContext = {
  onlyDirect: boolean;
  heldOutbound?: FlightHeldOutbound;
};

/** A ida já escolhida quando o pedido nasce na fase da volta. */
export type FlightHeldOutbound = {
  segment: {
    from: string;
    to: string;
    departAt: string;
    arriveAt: string;
    carrier: string;
    flightNumber: string;
    carrierName?: string | undefined;
  };
  price: { amount: number; currency: string };
  fareClass?: string | undefined;
};

/** Um evento de telemetria do explorer; o host decide para onde ele vai. */
export type FlightExplorerTrack = (
  event: string,
  props?: Record<string, string | number | boolean>,
) => void;

export type FlightExplorerEnv = {
  readonly copy: FlightExplorerCopy;
  readonly locale: string;
  readonly places: FlightExplorerPlaces;
  /** O classificador do texto do fornecedor (`polaritiesOf` do domain). */
  readonly classify: FareTextClassifier;
  /** Sem porta, a tela de tarifas não abre: a linha só seleciona. */
  readonly tariff?: FlightTariffPort | undefined;
  readonly track: FlightExplorerTrack;
  /** O drawer de detalhe do voo, que o host monta com as pernas e o mapa. */
  readonly renderDetail: RenderFlightDetail;
  /** Se a volta parte de outro aeroporto (#4203); a regra é do contrato. */
  readonly airportSwitchOf: FlightAirportSwitchFn;
  readonly dictation?: FlightFilterChipsProps['dictation'];
  /** Sem porta (a casca do chat), a lista não oferece o pedido offline. */
  readonly offlineRequest?: FlightOfflineRequestPort | undefined;
  /** Onde a fila de chips monta no palco estreito: a segunda linha da barra do
   *  host. Ausente ⇒ os controles ficam na linha da meta. */
  readonly toolbarSlot?: HTMLElement | null | undefined;
};

const ExplorerEnvContext = createContext<FlightExplorerEnv | null>(null);

export function ExplorerEnvProvider({
  env,
  children,
}: Readonly<{ env: FlightExplorerEnv; children: ReactNode }>) {
  return <ExplorerEnvContext.Provider value={env}>{children}</ExplorerEnvContext.Provider>;
}

/** O ambiente do explorer em cena. Fora da página é erro de montagem, não
 *  estado: toda peça daqui só existe dentro dela. */
export function useExplorerEnv(): FlightExplorerEnv {
  const env = useContext(ExplorerEnvContext);
  if (!env) throw new Error('flight explorer: peça montada fora da FlightExplorerPage');
  return env;
}
